/*
 * <order-protection> — custom order/shipping protection widget (replaces Navidium).
 * A hidden product carries 100 one-dollar variants ($1–$100); the fee is
 * ceil(subtotal × fee% / 100) dollars, where subtotal excludes the protection
 * line itself. All cart mutations go through ONE /cart/update.js call
 * ({oldVariant: 0, newVariant: 1} also swaps tiers atomically — update.js adds
 * variants that are not yet in the cart).
 * Pre-checked behavior: auto-adds once per cart when enabled, unless the
 * shopper has ever toggled it off (localStorage 'immense:protection-optout') —
 * one removal is remembered forever, it never re-adds itself.
 * Interactivity: toggle applies optimistic UI (state, sub-copy, checkout button
 * label swap to "Protected checkout") before the network round-trip; the drawer
 * re-renders via section render (cart page swaps its own section instead of
 * reloading) and the subtotal gets a brief accent flash on change.
 * Reconciliation is idempotent and re-runs on every cartUpdate pubsub event
 * (ignoring its own), so tier drift self-heals after any cart change.
 */
(function () {
	if (window.OrderProtectionLoaded) return;
	window.OrderProtectionLoaded = true;

	var OPTOUT_KEY = 'immense:protection-optout';
	var busy = false;

	function optedOut() {
		try { return localStorage.getItem(OPTOUT_KEY) === '1'; } catch (e) { return false; }
	}
	function setOptout(v) {
		try { v ? localStorage.setItem(OPTOUT_KEY, '1') : localStorage.removeItem(OPTOUT_KEY); } catch (e) {}
	}

	function getConfig() {
		var el = document.querySelector('order-protection');
		if (!el) return null;
		var map = {};
		try { map = JSON.parse(el.querySelector('script[data-op-map]').textContent); } catch (e) { return null; }
		var pid = parseInt(el.dataset.productId, 10);
		var pct = parseFloat(el.dataset.pct);
		if (!pid || !pct) return null;
		return { pid: pid, pct: pct, prechecked: el.dataset.prechecked === 'true', map: map };
	}

	function feeFor(subtotalCents, pct) {
		if (subtotalCents <= 0) return 0;
		var fee = Math.ceil((subtotalCents * pct) / 10000);
		return Math.max(1, Math.min(100, fee));
	}

	function cartPageSection() {
		var node = document.querySelector('[id^="shopify-section-"][id*="main-cart"]');
		if (!node) return null;
		return { node: node, id: node.id.replace('shopify-section-', '') };
	}

	function subtotalNodes() {
		return document.querySelectorAll('.cart-total-savings-row .totals__subtotal-value');
	}

	/* Optimistic UI: state attr, sub-copy and checkout button labels swap
	   immediately on toggle; the server render that follows corrects/confirms
	   everything (and restores the shield icon in the button label). */
	function applyOptimistic(el, on) {
		el.setAttribute('data-on', on ? 'true' : 'false');
		var sub = el.querySelector('.op-sub');
		if (sub) sub.textContent = on ? (el.dataset.subOn || sub.textContent) : (el.dataset.subOff || sub.textContent);
		var def = el.dataset.labelDefault;
		var prot = el.dataset.labelProtected;
		if (def && prot) {
			document.querySelectorAll('.cart__checkout-button .word, .cart__checkout-button > .button-label').forEach(function (n) {
				n.textContent = on ? prot : def;
			});
		}
		subtotalNodes().forEach(function (n) { n.classList.add('op-updating'); });
	}

	function flashSubtotals() {
		subtotalNodes().forEach(function (n) {
			n.classList.remove('op-updating');
			n.classList.add('op-flash');
			setTimeout(function () { n.classList.remove('op-flash'); }, 900);
		});
	}

	function setBusy(v) {
		busy = v;
		document.querySelectorAll('order-protection').forEach(function (el) {
			if (v) el.setAttribute('data-busy', 'true');
			else el.removeAttribute('data-busy');
		});
	}

	function mutate(updates) {
		var body = { updates: updates };
		var sections = [];
		var drawer = document.querySelector('cart-drawer');
		if (drawer && drawer.getSectionsToRender) {
			sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
		}
		var cartSec = cartPageSection();
		if (cartSec && sections.indexOf(cartSec.id) === -1) sections.push(cartSec.id);
		if (sections.length) {
			body.sections = sections;
			body.sections_url = window.location.pathname;
		}
		return fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
			body: JSON.stringify(body)
		})
			.then(function (r) { return r.json(); })
			.then(function (response) {
				if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
					publish(PUB_SUB_EVENTS.cartUpdate, { source: 'order-protection' });
				}
				if (drawer && drawer.renderContents && response.sections) {
					drawer.renderContents(response);
				}
				if (cartSec && response.sections && response.sections[cartSec.id]) {
					var temp = document.createElement('div');
					temp.innerHTML = response.sections[cartSec.id];
					var fresh = temp.querySelector('#' + cartSec.node.id) || temp.firstElementChild;
					if (fresh) cartSec.node.innerHTML = fresh.innerHTML;
				} else if (!drawer && document.body.classList.contains('body-template-cart')) {
					window.location.reload();
				}
				setTimeout(flashSubtotals, 60);
				return response;
			});
	}

	function reconcile() {
		if (busy) return;
		var c = getConfig();
		if (!c) return;
		setBusy(true);
		fetch('/cart.js')
			.then(function (r) { return r.json(); })
			.then(function (cart) {
				var line = null;
				var othersCount = 0;
				for (var i = 0; i < cart.items.length; i++) {
					if (cart.items[i].product_id === c.pid) line = cart.items[i];
					else othersCount++;
				}
				var updates = {};
				var dirty = false;

				if (!othersCount) {
					// protection must never be the only thing in the cart
					if (line) { updates[line.variant_id] = 0; dirty = true; }
				} else {
					var subtotal = cart.total_price - (line ? line.final_line_price : 0);
					var fee = feeFor(subtotal, c.pct);
					var wanted = c.map[String(fee)];
					if (line) {
						if (wanted && line.variant_id !== wanted) {
							updates[line.variant_id] = 0;
							updates[wanted] = 1;
							dirty = true;
						} else if (line.quantity !== 1) {
							updates[line.variant_id] = 1;
							dirty = true;
						}
					} else if (c.prechecked && !optedOut() && wanted) {
						updates[wanted] = 1;
						dirty = true;
					}
				}

				if (!dirty) { setBusy(false); return; }
				return mutate(updates).then(function () { setBusy(false); });
			})
			.catch(function () { setBusy(false); });
	}

	function toggle(on) {
		if (busy) return;
		var c = getConfig();
		if (!c) return;
		if (on) {
			setOptout(false);
			reconcile();
		} else {
			setOptout(true);
			setBusy(true);
			fetch('/cart.js')
				.then(function (r) { return r.json(); })
				.then(function (cart) {
					var line = null;
					for (var i = 0; i < cart.items.length; i++) {
						if (cart.items[i].product_id === c.pid) line = cart.items[i];
					}
					if (!line) { setBusy(false); return; }
					var updates = {};
					updates[line.variant_id] = 0;
					return mutate(updates).then(function () { setBusy(false); });
				})
				.catch(function () { setBusy(false); });
		}
	}

	customElements.define(
		'order-protection',
		class OrderProtection extends HTMLElement {
			connectedCallback() {
				var self = this;
				var btn = this.querySelector('.op-toggle');
				if (btn && !btn.dataset.bound) {
					btn.dataset.bound = 'true';
					btn.addEventListener('click', function () {
						if (busy) return;
						var turnOn = self.getAttribute('data-on') !== 'true';
						btn.setAttribute('aria-checked', turnOn ? 'true' : 'false');
						applyOptimistic(self, turnOn);
						toggle(turnOn);
					});
				}
			}
		}
	);

	if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
		subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
			if (event && event.source === 'order-protection') return;
			reconcile();
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', reconcile);
	} else {
		reconcile();
	}
})();
